import { useCallback, useEffect, useRef } from 'react';
import { type EventsType, useIdleTimer } from 'react-idle-timer';
import { IDLE_TIMEOUT_LOGOUT_EVENT } from '../constants';

// startIdleTimer, stopIdleTimer links are unstable which may result in unwanted useEffect re-runs on re-render
export const useIdleTimeout = (timeoutInMins: number, handleIdle: () => void) => {
  const timeoutMs = timeoutInMins * 60 * 1000;
  const debugInterval = useRef<NodeJS.Timeout | null>(null);

  const idleTimer = useIdleTimer({
    timeout: timeoutMs,
    events: USER_ACTIVITIES_EVENTS,
    onIdle: () => {
      console.debug(`[Idle Timer] Idle triggered after ${timeoutInMins} minutes`);
      // Dispatch custom event to notify pages to clear unsaved changes state
      window.dispatchEvent(new CustomEvent(IDLE_TIMEOUT_LOGOUT_EVENT));
      handleIdle();
    },
    // Use throttle rather than debounce for mouse movements
    onAction: event => {
      // Only log for debugging
      if (event?.type === 'mousemove') {
        console.debug('[Idle Timer] Activity detected', event.type);
      }
    },
    name: 'idle-ui-timeout',
    stopOnIdle: true,
    startManually: true,
    throttle: 5000,
  });

  const startIdleTimer = useCallback(() => {
    console.debug(`[Idle Timer] Activating timer with ${timeoutInMins}min timeout`);
    idleTimer.reset();
    idleTimer.activate();

    // Debug timer remaining time periodically
    if (debugInterval.current) {
      clearInterval(debugInterval.current);
    }
    debugInterval.current = setInterval(() => {
      const remaining = idleTimer.getRemainingTime();
      console.debug(`[Idle Timer] Remaining: ${remaining / 1000}s`);
    }, 120000); // Log every 2 mins
  }, [idleTimer, timeoutInMins]);

  const stopIdleTimer = useCallback(() => {
    console.debug('[Idle Timer] Stopping timer');
    if (debugInterval.current) {
      clearInterval(debugInterval.current);
    }
    idleTimer.reset();
  }, [idleTimer]);

  useEffect(() => {
    return () => {
      if (debugInterval.current) {
        clearInterval(debugInterval.current);
      }
    };
  }, []);

  return { startIdleTimer, stopIdleTimer };
};

export const USER_ACTIVITIES_EVENTS: EventsType[] = [
  'keydown',
  'mousemove',
  'wheel',
  'mousewheel',
  'mousedown',
  'touchstart',
  'click', // Explicit clicks
  'scroll',
];
